// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { redirect } from 'react-router';
import { store } from '../../../store/store';
import { getAccessToken } from '../../../../web-utils/src/utils/storageUtils';

import {
  fetchCompanies,
  getCompanyById,
  fetchCompanyMembers,
  fetchCompanyInvites,
  fetchCompanyEquipmentTypes,
  fetchCompanyEquipment,
} from '../../../store/companies/companiesActions';

import {
  fetchCompanyEquipmentGroups,
  fetchEquipmentGroupTypes,
} from '../../../store/equipment/equipmentActions';

import { fetchStudioByUuid, fetchStudiosMembers } from '../../../store/studio/studioActions';
import { clearStudio, clearError } from '../../../store/studio/studioSlice';
import { isUserAdmin } from '../../../store/user/userSelectors';

// Prevent multiple simultaneous loads during launch (shouldRevalidate takes
// care of it after that)
let isFetching = false;

export async function companyLoader() {
  if (isFetching) {
    return null;
  }
  const accessToken = getAccessToken();

  // user unauthenticated redirect to login
  if (!accessToken) {
    return redirect('/login');
  }
  let state = store.getState();

  // get current company and studio from state
  let companyUuid = state.companies?.company?.company_uuid;

  const studioUuid = state.studio?.studio?.studio_uuid;

  // If company is not set, fetch companies and set the first company as current
  if (!companyUuid) {
    // Fetch companies and set the first company as current
    const companies = await store.dispatch(fetchCompanies());
    const companiesList = companies?.payload || [];

    // If user has no companies, redirect to account page
    if (companiesList.length === 0) {
      return redirect('/new-account');
    }

    // Automatically set company  as the current company
    companyUuid = companiesList[0]?.company_uuid;
    await store.dispatch(getCompanyById(companyUuid));
  }
  // Store the last companyUuid to detect change in shouldRevalidate
  companyLoader.lastCompanyUuid = companyUuid;
  // Refresh state to get the updated companies state
  state = store.getState();
  if (companyUuid) {
    // clear state  studio error
    store.dispatch(clearError());

    const companyStudios = state.companies?.company?.studio || [];
    const studioBelongsToCompany = companyStudios.some(s => s.studio_uuid === studioUuid);
    // If studio is not set, fetch the first studio of the company as default
    if (!studioUuid || !studioBelongsToCompany) {
      const studioUuid = companyStudios[0]?.studio_uuid;
      if (studioUuid) {
        // Fetch and set the studio
        const action = await store.dispatch(fetchStudioByUuid(studioUuid)).unwrap();
        // if fetch studio failed, clear studio state
        if (fetchStudioByUuid.rejected.match(action)) {
          store.dispatch(clearStudio());
        }
        // Fetch studio members
        await store.dispatch(fetchStudiosMembers(studioUuid)).unwrap();
      } else {
        // Company has no studios, clear studio state
        store.dispatch(clearStudio());
      }
    }
    // Fetch members/invites/company_equipment_type/company_equipment for the current company
    isFetching = true;

    await Promise.all([
      store.dispatch(fetchCompanyEquipment(companyUuid)).unwrap(),
      store.dispatch(fetchCompanyEquipmentTypes(companyUuid)).unwrap(),
      store.dispatch(fetchCompanyEquipmentGroups(companyUuid)).unwrap(),
      store.dispatch(fetchEquipmentGroupTypes()).unwrap(),
      store.dispatch(fetchCompanyMembers(companyUuid)).unwrap(),
    ]);
    if (isUserAdmin(state)) {
      await store.dispatch(fetchCompanyInvites(companyUuid)).unwrap();
    }
    isFetching = false;
  }

  return null;
}

companyLoader.hydrate = true;
